import { z } from 'zod';
import { payableStageSchema, workflowStageSchema } from '../constants/stages.js';
import { nonNegativePaiseSchema } from '../money/paise.js';
import { ASSIGNMENT_WARNING_CODES } from '../workflow/assignment-warnings.js';
import { isoDateStringSchema, objectIdSchema } from './common.js';
import { measurementSnapshotSchema } from './measurement-profiles.js';
import { dueStateSchema } from './workflow.js';

/**
 * Who is doing which piece of work (task 20) — and the two different products built on top of it.
 *
 * THE OWNER'S SHAPE (`assignmentSchema`) carries the money. It has to: the rate captured on a
 * job is what task 21 settles against, and an owner assigning work needs to see what the job is
 * going to cost before they hand it over.
 *
 * THE KARIGAR'S SHAPE (`myWorkItemSchema`) carries NONE of it, and that is the point of it being
 * a separate schema rather than a subset picked at the call site. A tailor's screen shows the
 * garment, the measurements, the photographs and the due date; it must never show a price, a
 * revenue figure or somebody else's rate. Writing the two shapes independently means the day a
 * field is added to the order item, nobody has to remember to keep it off the phone in the
 * karigar's hand — it simply is not in this schema.
 *
 * THE RATE IS CAPTURED, NOT REFERENCED. `ratePaise` on an assignment is the answer
 * `resolveRate()` (task 19) gave at the moment the work was handed over, copied onto the row. A
 * rate card that rises in April cannot change what a March job pays, because April's number is
 * not where March's job looks.
 */

// --- The states a job passes through -----------------------------------------------------------

/**
 * `reassigned` and `cancelled` are CLOSED states that keep the row: the original assignment is
 * never rewritten to point at somebody else, because "who was this given to first, and why did it
 * move?" is a question asked weeks later, usually while somebody is arguing about being paid.
 */
export const ASSIGNMENT_STATUSES = [
  'assigned',
  'in_progress',
  'completed',
  'reassigned',
  'cancelled',
] as const;
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];
export const assignmentStatusSchema = z.enum(ASSIGNMENT_STATUSES);

/** Statuses that mean the job is still somebody's to do. What "workload" counts. */
export const OPEN_ASSIGNMENT_STATUSES = ['assigned', 'in_progress'] as const;
export type OpenAssignmentStatus = (typeof OPEN_ASSIGNMENT_STATUSES)[number];

export function isOpenAssignment(status: AssignmentStatus): status is OpenAssignmentStatus {
  return (OPEN_ASSIGNMENT_STATUSES as readonly AssignmentStatus[]).includes(status);
}

/** Where a captured rate came from. `override` is the negotiated exception on one job. */
export const ASSIGNMENT_RATE_SOURCES = ['staff', 'shop_default', 'override'] as const;
export type AssignmentRateSource = (typeof ASSIGNMENT_RATE_SOURCES)[number];
export const assignmentRateSourceSchema = z.enum(ASSIGNMENT_RATE_SOURCES);

// --- How long the job is expected to take -------------------------------------------------------

/**
 * How this job ranks against the other jobs on the same person's table.
 *
 * NOT the same fact as the customer's order being marked urgent, which is a property of the bill
 * and reaches the karigar's screen as `priority`. This one is a property of the JOB: two garments
 * off the same urgent order can still be a "do this first" and a "do this when you get to it".
 */
export const ASSIGNMENT_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type AssignmentPriority = (typeof ASSIGNMENT_PRIORITIES)[number];
export const assignmentPrioritySchema = z.enum(ASSIGNMENT_PRIORITIES);

/** What a job that nobody has ranked is worth ranking as. */
export const DEFAULT_ASSIGNMENT_PRIORITY: AssignmentPriority = 'normal';

/**
 * EVERY DURATION IN THIS MODULE IS A WHOLE NUMBER OF MINUTES.
 *
 * No float hours, no `"2h 30m"` strings, in the database or on the wire. Hours:minutes is a
 * display format and an input convenience and nothing else — the moment a duration is allowed to
 * be 2.5 somewhere, somebody has to decide whether 2.5 is hours or minutes, and half of them will
 * decide wrong.
 */
export const ASSIGNMENT_ESTIMATE_MIN_MINUTES = 1;
/** One week of work on a single job. Anything beyond it is a typo, not a lehenga. */
export const ASSIGNMENT_ESTIMATE_MAX_MINUTES = 10_080;
/** A day's overtime. An allowance on the job, never a payment — pay lives on the rate. */
export const ASSIGNMENT_OT_MAX_MINUTES = 1_440;

export const estimatedMinutesSchema = z
  .number()
  .int('Give whole minutes')
  .min(ASSIGNMENT_ESTIMATE_MIN_MINUTES)
  .max(ASSIGNMENT_ESTIMATE_MAX_MINUTES);

export const otMinutesSchema = z
  .number()
  .int('Give whole minutes')
  .min(0)
  .max(ASSIGNMENT_OT_MAX_MINUTES);

/**
 * The four scheduling facts, all optional everywhere they are written.
 *
 * Optional is the whole migration story: every assignment already in the collection was written
 * without them, and each one has a default that means "nobody has said" — a null estimate rather
 * than a zero one, because "not estimated" and "takes no time" are different facts.
 */
export const assignmentScheduleFields = {
  estimatedMinutes: estimatedMinutesSchema.optional(),
  priority: assignmentPrioritySchema.optional(),
  splittable: z.boolean().optional(),
  otMinutes: otMinutesSchema.optional(),
} as const;

// --- Problems -----------------------------------------------------------------------------------

/**
 * The three things that actually stop a karigar mid-job, plus a way out for the fourth.
 *
 * The alternative to this list is a tailor who cannot proceed putting the garment down and saying
 * nothing until somebody notices — which is the failure this feature exists to prevent.
 */
export const ASSIGNMENT_PROBLEM_KINDS = [
  'wrong_measurement',
  'fabric_short',
  'damaged_piece',
  'other',
] as const;
export type AssignmentProblemKind = (typeof ASSIGNMENT_PROBLEM_KINDS)[number];
export const assignmentProblemKindSchema = z.enum(ASSIGNMENT_PROBLEM_KINDS);

export const ASSIGNMENT_NOTE_MAX_LENGTH = 300;
const noteSchema = z.string().trim().max(ASSIGNMENT_NOTE_MAX_LENGTH);

/** Required on a reassignment and on a rate override — both are things people argue about. */
export const assignmentReasonSchema = z
  .string()
  .trim()
  .min(3, 'Say why')
  .max(ASSIGNMENT_NOTE_MAX_LENGTH);

export const assignmentProblemSchema = z.object({
  kind: assignmentProblemKindSchema,
  note: z.string().nullable(),
  raisedAt: isoDateStringSchema,
  raisedByName: z.string().nullable(),
  resolvedAt: isoDateStringSchema.nullable(),
  resolvedByName: z.string().nullable(),
  resolutionNote: z.string().nullable(),
});
export type AssignmentProblem = z.infer<typeof assignmentProblemSchema>;

// --- Requests ------------------------------------------------------------------------------------

export const createAssignmentSchema = z.object({
  itemId: objectIdSchema,
  stage: payableStageSchema,
  assignedToUserId: objectIdSchema,
  /**
   * The negotiated exception: "this one's fiddly, ₹450 instead of ₹300". Optional, audited, and
   * it does not touch the rate card — the next job goes back to the card's answer.
   */
  ratePaise: nonNegativePaiseSchema.optional(),
  overrideReason: assignmentReasonSchema.optional(),
  ...assignmentScheduleFields,
});
export type CreateAssignmentInput = z.input<typeof createAssignmentSchema>;

/** The start of a day: eight cards selected on the board, all given to one person at one stage. */
export const bulkAssignSchema = z.object({
  itemIds: z.array(objectIdSchema).min(1, 'Choose at least one garment').max(100),
  stage: payableStageSchema,
  assignedToUserId: objectIdSchema,
  /** Whatever is given applies to EVERY row the call creates: one decision, eight garments. */
  ...assignmentScheduleFields,
});
export type BulkAssignInput = z.input<typeof bulkAssignSchema>;

/**
 * Changing the plan on a job that has already been handed out.
 *
 * Any subset, but not the empty one: a request that names no field is a mistake somewhere in the
 * caller, and answering it with "saved" would hide that. This is scheduling and not money, so the
 * route behind it is guarded by `assignments.write` and NOT by the rate-override permission.
 */
export const updateAssignmentScheduleSchema = z
  .object(assignmentScheduleFields)
  .refine((value) => Object.values(value).some((one) => one !== undefined), {
    message: 'Change at least one thing',
  });
export type UpdateAssignmentScheduleInput = z.input<typeof updateAssignmentScheduleSchema>;

export const reassignSchema = z.object({
  assignedToUserId: objectIdSchema,
  reason: assignmentReasonSchema,
});
export type ReassignInput = z.input<typeof reassignSchema>;

export const overrideAssignmentRateSchema = z.object({
  ratePaise: nonNegativePaiseSchema,
  reason: assignmentReasonSchema,
});
export type OverrideAssignmentRateInput = z.input<typeof overrideAssignmentRateSchema>;

export const flagAssignmentProblemSchema = z.object({
  kind: assignmentProblemKindSchema,
  note: noteSchema.optional(),
});
export type FlagAssignmentProblemInput = z.input<typeof flagAssignmentProblemSchema>;

export const resolveAssignmentProblemSchema = z.object({
  note: noteSchema.optional(),
});

export const assignmentQuerySchema = z.object({
  itemId: objectIdSchema.optional(),
  orderId: objectIdSchema.optional(),
  assignedToUserId: objectIdSchema.optional(),
  stage: payableStageSchema.optional(),
  status: assignmentStatusSchema.optional(),
  /** Only jobs somebody still has to do. The default view of a shop floor. */
  openOnly: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value) => value === true || value === 'true' || value === '1'),
  /** Only jobs with an unresolved problem raised against them. */
  problemsOnly: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value) => value === true || value === 'true' || value === '1'),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type AssignmentQuery = z.infer<typeof assignmentQuerySchema>;

// --- The owner's view ------------------------------------------------------------------------------

export const assignmentSchema = z.object({
  id: objectIdSchema,
  itemId: objectIdSchema,
  orderId: objectIdSchema,
  orderNumber: z.string(),
  lineNumber: z.number().int().positive(),
  garmentTypeKey: z.string(),
  garmentTypeLabel: z.string(),
  customerName: z.string(),
  /** Copied from the item at assignment time — the number of pieces this rate is paid per. */
  quantity: z.number().int().positive(),
  stage: payableStageSchema,
  assignedToUserId: objectIdSchema,
  /** Snapshotted, so a karigar who leaves the shop does not blank out last year's ledger. */
  assignedToName: z.string(),
  assignedByName: z.string().nullable(),
  assignedAt: isoDateStringSchema,
  status: assignmentStatusSchema,
  /** `null` when nothing on the rate card prices this combination — "not priced yet", not free. */
  ratePaise: nonNegativePaiseSchema.nullable(),
  rateSource: assignmentRateSourceSchema.nullable(),
  /** `rate x quantity`, frozen with the rate. What task 21 settles. */
  earningPaise: nonNegativePaiseSchema.nullable(),
  rateOverridden: z.boolean(),
  rateOverrideReason: z.string().nullable(),
  /** Whole minutes of expected work. `null` is "nobody has estimated this", not "no time". */
  estimatedMinutes: estimatedMinutesSchema.nullable(),
  /** This job's own rank on the person's table. Defaults to `normal` on rows written before it. */
  priority: assignmentPrioritySchema,
  /** May this be broken across days or across people, or is it one sitting by one person? */
  splittable: z.boolean(),
  /** The overtime ALLOWANCE on this job, in whole minutes. Not a payment. */
  otMinutes: otMinutesSchema,
  startedAt: isoDateStringSchema.nullable(),
  completedAt: isoDateStringSchema.nullable(),
  /** Written by task 21 when the earning is paid. Null here means "earned, not yet settled". */
  settledAt: isoDateStringSchema.nullable(),
  /** Set on the row this one replaced, and on the row that replaced it. */
  reassignedAt: isoDateStringSchema.nullable(),
  reassignReason: z.string().nullable(),
  previousAssignmentId: objectIdSchema.nullable(),
  problem: assignmentProblemSchema.nullable(),
  promisedDate: isoDateStringSchema.nullable(),
  createdAt: isoDateStringSchema,
});
export type Assignment = z.infer<typeof assignmentSchema>;

export const assignmentWarningSchema = z.object({
  code: z.enum(ASSIGNMENT_WARNING_CODES),
  message: z.string(),
});

/**
 * The assignment, plus anything worth saying about it. The warnings never block — an assignment
 * that came back with two warnings still happened, and the response carries the row to prove it.
 */
export const assignmentResultSchema = z.object({
  assignment: assignmentSchema,
  warnings: z.array(assignmentWarningSchema),
});
export type AssignmentResult = z.infer<typeof assignmentResultSchema>;

export const assignmentListSchema = z.object({
  items: z.array(assignmentSchema),
});
export type AssignmentList = z.infer<typeof assignmentListSchema>;

export const skippedAssignmentSchema = z.object({
  itemId: objectIdSchema,
  reason: z.string(),
  message: z.string(),
});

/** Nothing is silently dropped: eight selected, six assigned, two explained. */
export const bulkAssignResultSchema = z.object({
  created: z.array(assignmentSchema),
  skipped: z.array(skippedAssignmentSchema),
  warnings: z.array(assignmentWarningSchema),
});
export type BulkAssignResult = z.infer<typeof bulkAssignResultSchema>;

// --- Workload -------------------------------------------------------------------------------------

/**
 * What is already on each person's table, so work is distributed rather than dumped.
 *
 * Deliberately job COUNTS and not rupees. This is the panel an owner reads while deciding who
 * gets the next lehenga, and money on it would answer a different question badly.
 */
export const staffWorkloadSchema = z.object({
  userId: objectIdSchema,
  name: z.string(),
  active: z.boolean(),
  specialisations: z.array(payableStageSchema),
  openCount: z.number().int().nonnegative(),
  inProgressCount: z.number().int().nonnegative(),
  overdueCount: z.number().int().nonnegative(),
  dueTodayCount: z.number().int().nonnegative(),
  completedTodayCount: z.number().int().nonnegative(),
  problemCount: z.number().int().nonnegative(),
});
export type StaffWorkload = z.infer<typeof staffWorkloadSchema>;

export const workloadListSchema = z.object({
  items: z.array(staffWorkloadSchema),
  /** Echoed back so a picker can show "busy" against the same number the server used. */
  overloadThreshold: z.number().int().positive(),
});
export type WorkloadList = z.infer<typeof workloadListSchema>;

// --- The karigar's view (NO money, ever) ---------------------------------------------------------

export const MY_WORK_FILTERS = ['open', 'today', 'done'] as const;
export type MyWorkFilter = (typeof MY_WORK_FILTERS)[number];

export const myWorkQuerySchema = z.object({
  filter: z.enum(MY_WORK_FILTERS).default('open'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type MyWorkQuery = z.infer<typeof myWorkQuerySchema>;

/**
 * One job as the person doing it sees it.
 *
 * Every field here is one a karigar needs in their hand to stitch the garment: what it is, how
 * many, what the measurements are, what the photographs show, what was asked for, and when it is
 * due. There is no price, no balance, no rate and no customer phone number — and
 * `customerFirstName` rather than the full name, because the tailor needs to tell two lehengas
 * apart, not to be handed the shop's customer book.
 */
export const myWorkItemSchema = z.object({
  assignmentId: objectIdSchema,
  itemId: objectIdSchema,
  orderId: objectIdSchema,
  orderNumber: z.string(),
  lineNumber: z.number().int().positive(),
  garmentTypeKey: z.string(),
  garmentTypeLabel: z.string(),
  quantity: z.number().int().positive(),
  customerFirstName: z.string(),
  stage: payableStageSchema,
  /** Where the garment itself currently is, which may be behind the stage of this job. */
  itemStage: workflowStageSchema,
  status: assignmentStatusSchema,
  promisedDate: isoDateStringSchema.nullable(),
  dueState: dueStateSchema,
  /**
   * THE CUSTOMER'S ORDER IS MARKED URGENT. A property of the bill, unchanged since it was added,
   * and deliberately NOT the job's own rank — see `jobPriority` below, which is a different fact
   * with a different name because renaming this one would silently change what a phone shows.
   */
  priority: z.boolean(),
  /** How long this job was expected to take, in whole minutes. `null` when nobody estimated it. */
  estimatedMinutes: estimatedMinutesSchema.nullable(),
  /** This JOB's rank against the other jobs on this person's table. */
  jobPriority: assignmentPrioritySchema,
  /** The overtime allowance on the job. An allowance is not a payment, so it may be shown here. */
  otMinutes: otMinutesSchema,
  measurements: measurementSnapshotSchema.nullable(),
  /** What the counter wrote on this garment. The "special instructions" line. */
  notes: z.string().nullable(),
  /** Design references, by id — the same signed-URL-on-demand rule the board uses. */
  mediaIds: z.array(objectIdSchema),
  assignedAt: isoDateStringSchema,
  startedAt: isoDateStringSchema.nullable(),
  completedAt: isoDateStringSchema.nullable(),
  problem: assignmentProblemSchema.nullable(),
});
export type MyWorkItem = z.infer<typeof myWorkItemSchema>;

export const myWorkListSchema = z.object({
  items: z.array(myWorkItemSchema),
  counts: z.object({
    open: z.number().int().nonnegative(),
    today: z.number().int().nonnegative(),
    overdue: z.number().int().nonnegative(),
    completedToday: z.number().int().nonnegative(),
  }),
});
export type MyWorkList = z.infer<typeof myWorkListSchema>;

/** Starting or finishing a job, and what it did to the garment on the board. */
export const workActionResultSchema = z.object({
  item: myWorkItemSchema,
  /** True when the garment moved a stage as a result. False when somebody had moved it already. */
  stageAdvanced: z.boolean(),
  stage: workflowStageSchema,
});
export type WorkActionResult = z.infer<typeof workActionResultSchema>;
